// Dialog "Conferma rimborso": chiude la revisione di una nota di credito
// (l'importo non e' derivabile in automatico dai sync, lo conferma Davide —
// nc_detect.py). Dopo conferma la proposta torna "da_fatturare" e passa dal
// solito MarkDialog per la sigla ST/RC/RCO. Stesso pattern modale.
import { useEffect, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { formatMoney, todayLocalISO } from "@/shared/format";
import { confirmNc } from "../api";
import type { Proposal } from "../types";

export function NcConfirmDialog({
  proposal,
  onDone,
  onClose,
}: {
  proposal: Proposal;
  onDone: () => void;
  onClose: () => void;
}) {
  // DUE fonti diverse, e chiamarle allo stesso modo diceva il falso.
  //  · `refund_amount` c'e' quando il canale ha riportato un rimborso PARZIALE
  //    (giro rimborsi): e' gia' l'importo giusto.
  //  · altrimenti `gross_expected` e' il lordo dell'emissione originale, che su
  //    un rimborso TOTALE (nc_detect) e' il default corretto.
  // Su uno storno aperto a mano non c'e' nessuna delle due: campo VUOTO, che e'
  // l'unica risposta onesta — l'importo si verifica sull'ordine del
  // marketplace, mai su un numero pre-compilato (regola HARD #19).
  const rimborsoNoto = proposal.refund_amount != null ? Number(proposal.refund_amount) : null;
  const lordoFattura = proposal.gross_expected != null ? Number(proposal.gross_expected) : null;
  const suggested = rimborsoNoto ?? lordoFattura;
  const [amount, setAmount] = useState(suggested != null ? String(suggested) : "");
  const [refundDate, setRefundDate] = useState(proposal.refund_date ?? todayLocalISO());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ESC per annullare (allineato a CallLogDialog). Ignorato durante l'invio.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const submit = async () => {
    const value = Number(amount);
    if (!amount.trim() || !Number.isFinite(value) || value <= 0) {
      setError("L'importo rimborso è obbligatorio e deve essere maggiore di zero.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await confirmNc(proposal.id, { refund_amount: value, refund_date: refundDate });
      onDone();
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Conferma rimborso"
      data-no-open
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="w-full max-w-sm rounded-lg border border-line bg-card p-5 shadow-popover">
        <h3 className="font-display text-[16px] font-bold text-ink">
          Conferma rimborso — {proposal.customer_name}
        </h3>
        {rimborsoNoto != null ? (
          <p className="mt-1 text-[11px] text-ink-2">
            Rimborso letto dal canale: {formatMoney(rimborsoNoto)} — verificalo sull&apos;ordine del
            marketplace
          </p>
        ) : (
          lordoFattura != null && (
            <p className="mt-1 text-[11px] text-ink-2">
              Fattura originale: {formatMoney(lordoFattura)}
            </p>
          )
        )}

        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-2">
            Importo rimborso
            <input
              type="number"
              step={0.01}
              min={0}
              inputMode="decimal"
              value={amount}
              disabled={busy}
              autoFocus
              onChange={(e) => setAmount(e.target.value)}
              className="h-[44px] rounded-md border border-line bg-card px-3 text-[13px] font-normal tabular-nums text-ink outline-none focus:border-accent disabled:opacity-50"
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-2">
            Data rimborso
            <input
              type="date"
              value={refundDate}
              disabled={busy}
              onChange={(e) => setRefundDate(e.target.value)}
              className="h-[44px] rounded-md border border-line bg-card px-3 text-[13px] font-normal text-ink outline-none focus:border-accent disabled:opacity-50"
            />
          </label>
        </div>

        {error && (
          <p role="alert" className="mt-3 text-[12px] text-red">
            {error}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-line bg-card px-3 py-2 text-[12px] font-medium text-ink-2 hover:border-ink-4 disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void submit()}
            className="rounded-md border border-accent bg-accent-2 px-3 py-2 text-[12px] font-medium text-white disabled:opacity-50"
          >
            {busy ? "Salvo…" : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}
